import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";

// Icons
import { RxDashboard } from "react-icons/rx";
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { IoCreateOutline } from "react-icons/io5";
import { CiBank, CiSettings } from "react-icons/ci";
import { LuWarehouse } from "react-icons/lu";
import { TbReport } from "react-icons/tb";
import { MdSupportAgent } from "react-icons/md";
import { RiGroupLine } from "react-icons/ri";
import { CgProfile } from "react-icons/cg";
import { GiBrain } from "react-icons/gi";
import { LogOut } from "lucide-react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useMyContext } from "../../../context/MyContext";
import "../../../css/custom-scrollbar.css";
import { useAuth } from "../../../routes/Auth";

const Sidebar = () => {
  const { profile_flag } = useMyContext();
  const [activeTab, setActiveTab] = useState(-1);
  const [pageTab, setPageTab] = useState(-1);
  const [activeSubTab, setActiveSubTab] = useState(-1);
  const { isToggleSidebar, setIsToggleSidebar, toggleB2BMode } =
    useMyContext();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const sidebarRef = useRef(null);
  const userRole = localStorage.getItem("role");

  // ============================================================
  // MENU TOGGLE
  // ============================================================

  const toggleSubmenu = useCallback(
    (index, href) => {
      if (profile_flag === "0" && userRole !== "admin") {
        toast.error(
          "Please Contact to the Support Team, Your KYC is Pending"
        );
        return;
      }
      if (href) {
        navigate(href);
      } else {
        setActiveTab((prevActiveTab) =>
          prevActiveTab === index ? null : index
        );
      }
    },
    [profile_flag, userRole, navigate, setActiveTab]
  );

  // ============================================================
  // MENU ITEMS
  // ============================================================

  let menuItems = [
    {
      title: "Dashboard",
      icon: <RxDashboard />,
      href: "/b2b/dashboard",
      submenu: [],
    },
    {
      title: "Shipments",
      icon: <IoCreateOutline />,
      submenu: [
        { title: "Forward Shipment", href: "/b2b/shipments/forward" },
        { title: "Track Shipments", href: "/b2b/shipment/track" },
        { title: "View Shipments", href: "/b2b/shipments/list" },
        { title: "Pickup Request", href: "/b2b/shipment/pickuprequest" },
      ],
    },
    {
      title: "Warehouse",
      icon: <LuWarehouse />,
      submenu: [
        { title: "Add Warehouse", href: "/b2b/warehouse/add" },
        { title: "All Warehouses", href: "/b2b/warehouse/all" },
      ],
    },
    {
      title: "Payments",
      icon: <CiBank />,
      submenu: [
        { title: "Wallet Balance", href: "/b2b/payments/wallet_recharge" },
      ],
    },
    {
      title: "Customers",
      icon: <RiGroupLine />,
      submenu: [
        { title: "ViewAllCustomer", href: "/b2b/customers/viewCustomer" },
        { title: "KYC Pending", href: "/b2b/customers/kycPending" },
      ],
    },
    {
      title: "Preship",
      icon: <GiBrain />,
      submenu: [
        { title: "Rate Calculator", href: "/b2b/preship/PriceEstimator" },
        { title: "Rate Card", href: "/b2b/preship/rateCard/" },
        { title: "Set Rate", href: "/b2b/preship/setRate" },
      ],
    },
    {
      title: "Reports",
      href: "/b2b/reports",
      icon: <TbReport />,
      submenu: [],
    },
    {
      title: "Help",
      href: "/b2b/help",
      icon: <MdSupportAgent />,
      submenu: [],
    },
    {
      title: "Setting",
      icon: <CiSettings />,
      submenu: [
        { title: "Add Employee", href: "b2b/addEmployee" },
        { title: "All Employee", href: "b2b/allEmployee" },
      ],
    },
    {
      title: "Profile",
      icon: <CgProfile />,
      href: "/b2b/profile",
      submenu: [],
    },
  ];

  // ============================================================
  // ROLE BASED MENU
  // ============================================================

  if (userRole !== "admin") {
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Weight Upload"
        );
      }
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "All Weight Disputes"
        );
      }
    });
    menuItems = menuItems.filter((item) => item.title !== "Customers");
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Set Rate"
        );
      }
    });
    menuItems = menuItems.filter((item) => item.title !== "Setting");
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Add Role") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Add Role"
        );
      }
    });
  } else {
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Rate Card"
        );
      }
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Rate Calculator"
        );
      }
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Weight Disputes"
        );
      }
    });
  }

  // ============================================================
  // ACTIVE ROUTE
  // ============================================================

  useMemo(() => {
    let currRoute = window.location.pathname;
    menuItems.map((item, index) => {
      if (item.href && currRoute == item.href) {
        setActiveTab(index);
        setPageTab(index);
      }
      if (item.submenu) {
        item.submenu.map((subItem, subIndex) => {
          if (subItem.href && currRoute == subItem.href) {
            setActiveTab(index);
            setActiveSubTab(subIndex);
            setPageTab(index);
          }
        });
      }
    });
  }, []);

  // ============================================================
  // MOBILE OUTSIDE CLICK
  // ============================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth <= 768
      ) {
        setIsToggleSidebar(false);
      }
    };
    if (isToggleSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isToggleSidebar, setIsToggleSidebar]);

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    toggleB2BMode(false);
    localStorage.removeItem("user");
    navigate("/login");
    logout();
    localStorage.removeItem("cus_id");
    localStorage.removeItem("token");
    toast.success("LogOut Sucessfully!");
    window.location.reload();
  };

  // ============================================================
  // SIDEBAR
  // ============================================================

  return (
    <>
      <ToastContainer 
      position="top-right" autoClose={1000}
       />
      {isToggleSidebar && (
        <aside
          ref={sidebarRef}
          className="
            relative
            w-64
            h-full
            flex
            flex-col
            overflow-hidden
            text-gray-700
            sidebar-glass
          "
        >
          {/* =====================================================
              CLEAN WHITE BACKGROUND
          ===================================================== */}

          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-white" />

            <div
              className="
                absolute
                top-0
                left-0
                right-0
                h-24
                bg-gradient-to-b
                from-gray-50/80
                via-white/40
                to-transparent
              "
            />

            <div
              className="
                absolute
                top-0
                right-0
                bottom-0
                w-px
                bg-gray-200/70
              "
            />

            <div
              className="
                absolute
                bottom-0
                left-0
                right-0
                h-16
                bg-gradient-to-t
                from-gray-900/[0.02]
                to-transparent
              "
            />
          </div>

          {/* =====================================================
              CONTENT
          ===================================================== */}

          <div className="relative z-10 flex flex-col h-full">
            {/* =================================================
                MENU CONTAINER
            ================================================= */}

            <div className="flex-1 overflow-hidden">
              <div
                className="
                  h-full
                  overflow-y-auto
                  px-3
                  pt-4
                "
              >
                <ul className="">
                  {menuItems.map((item, index) => {
                    const isActive = activeTab === index;

                    return (
                      <li key={index} className="group">
                        {/* =================================================
                            MAIN MENU BUTTON
                            - Default: flat, no border
                            - Active / Hover: soft floating white card
                        ================================================= */}

                        <Button
                          onClick={() => toggleSubmenu(index, item.href)}
                          sx={{
                            minWidth: 0,

                            fontFamily:
                              "var(--font-custom-open-sans)",

                            textTransform: "none",

                            width: "100%",

                            textAlign: "left",

                            fontWeight: 500,

                            display: "flex",

                            alignItems: "center",

                            gap: "10px",

                            padding: "9px 12px",

                            borderRadius: "16px",

                            position: "relative",

                            overflow: "hidden",

                            transition:
                              "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",

                            color: isActive
                              ? "#1f2937"
                              : "rgba(75,85,99,0.75)",

                            /*
                             * DEFAULT — flat, invisible
                             */

                            background: isActive
                              ? "linear-gradient(90deg, rgba(255,255,255,0.95), rgba(243,244,246,0.8), rgba(229,231,235,0.65))"
                              : "transparent",

                            border: isActive
                              ? "1px solid rgba(0,0,0,0.04)"
                              : "1px solid transparent",

                            /*
                             * Soft floating card shadow
                             */

                            boxShadow: isActive
                              ? "0 1px 2px rgba(16,24,40,0.04), 0 4px 10px rgba(16,24,40,0.06), 0 10px 24px rgba(16,24,40,0.05)"
                              : "none",

                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(255,255,255,0.95), rgba(243,244,246,0.8), rgba(229,231,235,0.65))",

                              border:
                                "1px solid rgba(0,0,0,0.04)",

                              color: "#1f2937",

                              boxShadow:
                                "0 1px 2px rgba(16,24,40,0.04), 0 4px 10px rgba(16,24,40,0.06), 0 10px 24px rgba(16,24,40,0.05)",
                            },
                          }}
                        >
                          {/* =================================================
                              ICON — dark rounded badge
                          ================================================= */}

                          <span
                            className={`
                              relative
                              z-10
                              flex
                              items-center
                              justify-center
                              w-7
                              h-7
                              rounded-full
                              text-[14px]
                              shrink-0
                              transition-all
                              duration-300
                              ${
                                isActive
                                  ? "bg-gray-900 text-white group-hover:bg-gray-900"
                                  : "bg-transparent text-gray-400 group-hover:bg-gray-900 group-hover:text-white"
                              }
                            `}
                          >
                            {item.icon}
                          </span>

                          {/* =================================================
                              TITLE
                          ================================================= */}

                          <span
                            className={`
                              relative
                              z-10
                              flex-1
                              text-left
                              text-[12px]
                              font-medium
                              transition-colors
                              duration-300
                              ${
                                isActive
                                  ? "text-gray-800"
                                  : "text-gray-600 group-hover:text-gray-800"
                              }
                            `}
                          >
                            {item.title}
                          </span>

                          {/* =================================================
                              ARROW
                          ================================================= */}

                          {item.submenu.length > 0 && (
                            <span
                              className={`
                                relative
                                z-10
                                text-[9px]
                                mr-1
                                transition-all
                                duration-300
                                ${
                                  isActive
                                    ? "text-gray-500 rotate-180"
                                    : "text-gray-400 group-hover:text-gray-600"
                                }
                              `}
                            >
                              {isActive ? <FaAngleDown /> : <FaAngleRight />}
                            </span>
                          )}
                        </Button>

                        {/* =================================================
                            SUBMENU
                        ================================================= */}

                        {item.submenu.length > 0 && isActive && (
                          <div
                            className="
                              relative
                              ml-5
                              mt-1
                              mb-1
                            "
                          >
                            <ul
                              className="
                                relative
                                z-10
                                space-y-0.5
                                py-1
                                px-0.5
                              "
                            >
                              {item.submenu.map((subItem, subIndex) => {
                                const isSubActive =
                                  activeSubTab === subIndex;

                                return (
                                  <li key={subIndex}>
                                    <Link
                                      to={subItem.href}
                                      onClick={() =>
                                        setActiveSubTab(subIndex)
                                      }
                                      className={`
                                        flex
                                        items-center
                                        px-3
                                        py-2
                                        rounded-full
                                        text-[10px]
                                        font-medium
                                        transition-all
                                        duration-300
                                        border
                                        ${
                                          isSubActive
                                            ? "text-gray-800 bg-white border-black/[0.04] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)]"
                                            : "text-gray-500 border-transparent hover:text-gray-800 hover:bg-white hover:border-black/[0.04] hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)]"
                                        }
                                      `}
                                    >
                                      {/* Sub Active Dot */}

                                      {isSubActive && (
                                        <span
                                          className="
                                            w-1
                                            h-1
                                            rounded-full
                                            bg-gray-900
                                            mr-2
                                          "
                                        />
                                      )}

                                      <span>{subItem.title}</span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* =================================================
                LOGOUT
            ================================================= */}

            <div
              className="
                px-3
                pb-3
                pt-2
              "
            >
              {/* Separator */}

              <div
                className="
                  h-px
                  bg-gray-200/70
                  mb-3
                "
              />

              <button
                onClick={handleLogout}
                className="
                  group
                  relative
                  w-full
                  flex
                  items-center
                  justify-center
                  gap-2.5
                  px-3
                  py-2
                  rounded-2xl
                  text-left
                  text-[11px]
                  font-medium
                  text-white
                  bg-red-600
                  border
                  border-transparent
                  transition-all
                  duration-300
                  hover:bg-gray-300
                  hover:border-black/[0.04]
                  hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06),0_10px_24px_rgba(16,24,40,0.05)]
                  hover:text-gray-600
                  active:scale-[0.98]
                "
              >
                {/* Logout Icon */}

                <div
                  className="
                    flex
                    items-center
                    justify-center
                    w-7
                    h-7
                    rounded-full
                    border
                    border-gray-100/70
                    text-white
                     transition-all
                    duration-300
                    group-hover:bg-gray-900
                    group-hover:text-white
                    group-hover:border-transparent
                  "
                >
                  <LogOut color="white" size={13} />
                </div>

                <span className="text-[14px]">Logout</span>
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default Sidebar;